import Breadcrumb from '../../components/feature/Breadcrumb';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../hooks/useSEO';
import { Link, useLocation } from 'react-router-dom';

const FilamentPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en/');

  const TEXT_JA = {
    title: 'フィラメントデータ',
    description: 'FDM方式3Dプリンターで使用する主要フィラメントの設定値と特性データ。PLA、PETG、ABS、ASA、PC、PA、TPUなど各材料の推奨温度、特徴、使用例を実測データに基づき掲載。',
    keywords: '3Dプリンター,フィラメント,PLA,PETG,ABS,ASA,PC,PA,TPU,設定値,温度',
    breadcrumbHome: 'ホーム',
    breadcrumbFilament: 'フィラメントデータ',
    pageTitle: 'フィラメント一覧',
    intro1: 'FDM方式3Dプリンターで使用する主要フィラメントの設定値と特性データです。実際の造形テストに基づいた推奨設定を掲載しています。',
    intro2: '近年はカーボン繊維強化（CF）やガラス繊維強化（GF）素材なども普及しています。これらは主に元となるベースフィラメント（PLA、PETG、ABS、ナイロンなど）に繊維を混合した派生材料で、強度や剛性を高める代わりにノズル摩耗が増える傾向があります。',
    difficultyBeginner: '初級',
    difficultyIntermediate: '中級',
    difficultyAdvanced: '上級',
    difficultyBeginnerDesc: '造形が容易',
    difficultyIntermediateDesc: '設定調整が必要',
    difficultyAdvancedDesc: '高度な技術が必要',
    tableHeaderMaterial: '材料名',
    tableHeaderDifficulty: '難易度',
    tableHeaderNozzle: 'ノズル温度',
    tableHeaderBed: 'ベッド温度',
    tableHeaderFeatures: '特徴',
    mobileNozzle: 'ノズル:',
    mobileBed: 'ベッド:',
    mobileDetail: '詳細を見る',
    deprecatedTitle: '非推奨材料（参考情報）',
    deprecatedIntro: '以下の材料は造形難易度が極めて高い、または安全上の問題があるため、一般的な使用は推奨されません。',
    downloadTitle: 'データダウンロード',
    downloadDesc1: '曲げ試験データ（CSV形式）をダウンロードできます。',
    downloadDesc2: '研究・学習・個人プロジェクトにご活用ください。',
    downloadButton: 'データダウンロードページへ',
    noteTitle: 'ご注意',
    note1: '温度設定はフィラメントメーカーや色によって異なる場合があります。',
    note2: '各材料名をクリックすると、詳細ページで派生材料（PLA+、PLA-CF等）の情報もご覧いただけます。',
    note3: '初めての材料は、テスト造形で設定を確認することをお勧めします。',
    filaments: {
      pla: { features: '最も使いやすい初心者向けフィラメント。反りが少なく、造形が容易。' },
      petg: { features: '透明性と強度を兼ね備え、耐薬品性に優れる。' },
      abs: { features: '高強度・耐熱性に優れた工業用フィラメント。反りやすい。' },
      asa: { features: 'UV耐性があり屋外使用に適したフィラメント。ABSの改良版。' },
      pc: { features: '非常に高い強度と耐熱性。高温造形が必要。' },
      pa: { features: '優れた靭性と耐摩耗性。吸湿性が高く保管に注意。' },
      tpu: { features: '柔軟性のあるゴム状フィラメント。低速造形が必要。' }
    },
    deprecated: {
      pp: { reason: '反りが非常に強く、ベッド密着が困難。専用の接着剤や高温ベッドが必須。' },
      pom: { reason: '造形時に有害なホルムアルデヒドを発生。換気設備が必須で家庭用には不向き。' },
      pmma: { reason: '脆く割れやすい。造形難易度が高く、実用性に乏しい。' }
    }
  };

  const TEXT_EN = {
    title: 'Filament Data',
    description: 'Settings and characteristics data for major filaments used in FDM 3D printers. Recommended temperatures, features, and usage examples for PLA, PETG, ABS, ASA, PC, PA, TPU, and other materials based on actual test data.',
    keywords: '3D printer,filament,PLA,PETG,ABS,ASA,PC,PA,TPU,settings,temperature',
    breadcrumbHome: 'Home',
    breadcrumbFilament: 'Filament Data',
    pageTitle: 'Filaments',
    intro1: 'Settings and characteristics data for major filaments used in FDM 3D printers. Recommended settings are based on actual printing tests.',
    intro2: 'In recent years, carbon fiber reinforced (CF) and glass fiber reinforced (GF) materials have become popular. These are derivative materials that mix fibers into base filaments (PLA, PETG, ABS, nylon, etc.), increasing strength and rigidity at the cost of increased nozzle wear.',
    difficultyBeginner: 'Beginner',
    difficultyIntermediate: 'Intermediate',
    difficultyAdvanced: 'Advanced',
    difficultyBeginnerDesc: 'Easy to print',
    difficultyIntermediateDesc: 'Requires tuning',
    difficultyAdvancedDesc: 'Advanced skills needed',
    tableHeaderMaterial: 'Material',
    tableHeaderDifficulty: 'Difficulty',
    tableHeaderNozzle: 'Nozzle Temp',
    tableHeaderBed: 'Bed Temp',
    tableHeaderFeatures: 'Features',
    mobileNozzle: 'Nozzle:',
    mobileBed: 'Bed:',
    mobileDetail: 'View Details',
    deprecatedTitle: 'Not Recommended Materials (Reference)',
    deprecatedIntro: 'The following materials are not recommended for general use due to extremely high printing difficulty or safety concerns.',
    downloadTitle: 'Data Download',
    downloadDesc1: 'Download bending test data (CSV format).',
    downloadDesc2: 'Use for research, learning, or personal projects.',
    downloadButton: 'Go to Data Download Page',
    noteTitle: 'Notes',
    note1: 'Temperature settings may vary depending on filament manufacturer and color.',
    note2: 'Click on each material name to view information about derivative materials (PLA+, PLA-CF, etc.) on the detail page.',
    note3: 'For new materials, we recommend confirming settings with test prints.',
    filaments: {
      pla: { features: 'Most user-friendly filament for beginners. Low warping, easy to print.' },
      petg: { features: 'Combines transparency and strength with excellent chemical resistance.' },
      abs: { features: 'Industrial filament with high strength and heat resistance. Prone to warping.' },
      asa: { features: 'UV-resistant filament suitable for outdoor use. Improved version of ABS.' },
      pc: { features: 'Very high strength and heat resistance. Requires high-temperature printing.' },
      pa: { features: 'Excellent toughness and wear resistance. High moisture absorption, requires careful storage.' },
      tpu: { features: 'Flexible rubber-like filament. Requires slow printing speed.' }
    },
    deprecated: {
      pp: { reason: 'Very strong warping, difficult bed adhesion. Requires dedicated adhesive or high-temperature bed.' },
      pom: { reason: 'Releases harmful formaldehyde during printing. Requires ventilation equipment, unsuitable for home use.' },
      pmma: { reason: 'Brittle and prone to cracking. High printing difficulty with limited practicality.' }
    }
  };

  const TEXT = isEnglish ? TEXT_EN : TEXT_JA;

  useSEO({
    title: TEXT.title,
    description: TEXT.description,
    keywords: TEXT.keywords,
    canonicalPath: isEnglish ? '/en/filament' : '/filament'
  });

  const breadcrumbItems = [
    { label: TEXT.breadcrumbHome, path: isEnglish ? '/en' : '/' },
    { label: TEXT.breadcrumbFilament }
  ];

  const prefix = isEnglish ? '/en' : '';

  // 基本材料（7種）
  const basicFilaments = [
    {
      name: 'PLA',
      difficulty: 'beginner',
      difficultyLabel: TEXT.difficultyBeginner,
      nozzleTemp: '190-220°C',
      bedTemp: '50-60°C',
      features: TEXT.filaments.pla.features,
      path: `${prefix}/filament/pla`
    },
    {
      name: 'PETG',
      difficulty: 'intermediate',
      difficultyLabel: TEXT.difficultyIntermediate,
      nozzleTemp: '220-250°C',
      bedTemp: '70-80°C',
      features: TEXT.filaments.petg.features,
      path: `${prefix}/filament/petg`
    },
    {
      name: 'ABS',
      difficulty: 'advanced',
      difficultyLabel: TEXT.difficultyAdvanced,
      nozzleTemp: '220-260°C',
      bedTemp: '80-100°C',
      features: TEXT.filaments.abs.features,
      path: `${prefix}/filament/abs`
    },
    {
      name: 'ASA',
      difficulty: 'advanced',
      difficultyLabel: TEXT.difficultyAdvanced,
      nozzleTemp: '240-260°C',
      bedTemp: '80-100°C',
      features: TEXT.filaments.asa.features,
      path: `${prefix}/filament/asa`
    },
    {
      name: 'PC',
      difficulty: 'advanced',
      difficultyLabel: TEXT.difficultyAdvanced,
      nozzleTemp: '260-310°C',
      bedTemp: '90-110°C',
      features: TEXT.filaments.pc.features,
      path: `${prefix}/filament/pc`
    },
    {
      name: isEnglish ? 'PA (Nylon)' : 'PA（ナイロン）',
      difficulty: 'advanced',
      difficultyLabel: TEXT.difficultyAdvanced,
      nozzleTemp: '240-270°C',
      bedTemp: '70-90°C',
      features: TEXT.filaments.pa.features,
      path: `${prefix}/filament/pa`
    },
    {
      name: 'TPU',
      difficulty: 'intermediate',
      difficultyLabel: TEXT.difficultyIntermediate,
      nozzleTemp: '210-230°C',
      bedTemp: '40-60°C',
      features: TEXT.filaments.tpu.features,
      path: `${prefix}/filament/tpu`
    }
  ];

  // 非推奨材料
  const deprecatedFilaments = [
    {
      name: 'PP',
      reason: TEXT.deprecated.pp.reason
    },
    {
      name: 'POM',
      reason: TEXT.deprecated.pom.reason
    },
    {
      name: isEnglish ? 'PMMA (Acrylic Resin)' : 'PMMA（アクリル系樹脂）',
      reason: TEXT.deprecated.pmma.reason
    }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8" data-lang={isEnglish ? 'en' : 'ja'}>
            {/* ページタイトル */}
            <div className="text-center mt-8">
              <h2 className="text-3xl font-bold text-white font-['Exo'] mb-4">
                {TEXT.pageTitle}
              </h2>
              <div className="section-divider max-w-md mx-auto mb-6"></div>
              <p className="text-metal-light max-w-3xl mx-auto leading-relaxed">
                {TEXT.intro1}
              </p>
              <p className="text-metal-light max-w-3xl mx-auto leading-relaxed mt-4">
                {TEXT.intro2}
              </p>
            </div>

            {/* Difficulty Legend */}
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center space-x-2">
                <span className="difficulty-beginner px-4 py-2 text-sm font-semibold whitespace-nowrap">{TEXT.difficultyBeginner}</span>
                <span className="text-metal-light text-sm">{TEXT.difficultyBeginnerDesc}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="difficulty-intermediate px-4 py-2 text-sm font-semibold whitespace-nowrap">{TEXT.difficultyIntermediate}</span>
                <span className="text-metal-light text-sm">{TEXT.difficultyIntermediateDesc}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="difficulty-advanced px-4 py-2 text-sm font-semibold whitespace-nowrap">{TEXT.difficultyAdvanced}</span>
                <span className="text-metal-light text-sm">{TEXT.difficultyAdvancedDesc}</span>
              </div>
            </div>

            {/* Desktop Table (md以上) */}
            <div className="tech-card overflow-hidden hidden md:block">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-[#3a3d42]">
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white font-['Exo']">
                        {TEXT.tableHeaderMaterial}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white font-['Exo']">
                        {TEXT.tableHeaderDifficulty}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white font-['Exo']">
                        {TEXT.tableHeaderNozzle}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white font-['Exo']">
                        {TEXT.tableHeaderBed}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white font-['Exo']">
                        {TEXT.tableHeaderFeatures}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-metal">
                    {basicFilaments.map((filament, index) => (
                      <tr 
                        key={index}
                        className="hover:bg-metal/30 transition-colors cursor-pointer"
                        onClick={() => window.REACT_APP_NAVIGATE(filament.path)}
                      >
                        <td className="px-6 py-4">
                          <span className="text-white font-semibold font-['Exo'] text-lg">
                            {filament.name}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`difficulty-${filament.difficulty} px-3 py-1 text-sm font-semibold whitespace-nowrap inline-block`}>
                            {filament.difficultyLabel}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-metal-light font-['Roboto_Mono']">
                            {filament.nozzleTemp}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-metal-light font-['Roboto_Mono']">
                            {filament.bedTemp}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-metal-light text-sm">
                            {filament.features}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card Layout (md未満) */}
            <div className="md:hidden space-y-3">
              {basicFilaments.map((filament, index) => (
                <div 
                  key={index}
                  className="tech-card p-4 cursor-pointer hover:border-accent transition-colors"
                  onClick={() => window.REACT_APP_NAVIGATE(filament.path)}
                >
                  {/* ヘッダー行：材料名 + 難易度 */}
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold font-['Exo'] text-lg">
                      {filament.name}
                    </h3>
                    <span className={`difficulty-${filament.difficulty} px-3 py-1 text-xs font-semibold whitespace-nowrap`}>
                      {filament.difficultyLabel}
                    </span>
                  </div>

                  {/* 温度情報：横並び */}
                  <div className="flex items-center gap-4 mb-3 text-xs">
                    <div className="flex items-center gap-1">
                      <span className="text-metal-light">{TEXT.mobileNozzle}</span>
                      <span className="text-white font-['Roboto_Mono']">{filament.nozzleTemp}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-metal-light">{TEXT.mobileBed}</span>
                      <span className="text-white font-['Roboto_Mono']">{filament.bedTemp}</span>
                    </div>
                  </div>

                  {/* 特徴：2行まで */}
                  <p className="text-metal-light text-xs line-clamp-2">
                    {filament.features}
                  </p>

                  {/* 詳細へのリンク表示 */}
                  <div className="mt-2 flex items-center justify-end">
                    <span className="text-accent text-xs flex items-center gap-1">
                      {TEXT.mobileDetail}
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Deprecated Materials */}
            <div className="static-card p-6">
              <h3 className="text-lg font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.deprecatedTitle}
              </h3>
              <p className="text-metal-light text-sm mb-4">
                {TEXT.deprecatedIntro}
              </p>
              <div className="space-y-3">
                {deprecatedFilaments.map((material, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-metal/20 border-l-4 border-accent-orange">
                    <i className="ri-alert-line text-accent-orange mt-1"></i>
                    <div>
                      <h4 className="text-white font-semibold font-['Exo'] mb-1">
                        {material.name}
                      </h4>
                      <p className="text-metal-light text-sm">
                        {material.reason}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* データダウンロードへのリンク */}
            <div className="metal-panel p-6 text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <i className="ri-download-cloud-line text-accent text-3xl"></i>
                <h2 className="text-xl font-semibold text-white font-['Exo']">
                  {TEXT.downloadTitle}
                </h2>
              </div>
              <p className="text-metal-light text-sm mb-6">
                {TEXT.downloadDesc1}<br />
                {TEXT.downloadDesc2}
              </p>
              <Link
                to={`${prefix}/filament/data-download`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent hover:bg-accent-orange text-graphite font-semibold rounded transition-colors whitespace-nowrap"
              >
                <i className="ri-download-2-line"></i>
                {TEXT.downloadButton}
              </Link>
            </div>

            {/* Note */}
            <div className="metal-panel p-6">
              <div className="flex items-start space-x-3">
                <i className="ri-information-line text-accent text-xl mt-1"></i>
                <div className="space-y-2">
                  <h4 className="text-white font-semibold font-['Exo']">
                    {TEXT.noteTitle}
                  </h4>
                  <ul className="text-metal-light text-sm space-y-1 list-disc list-inside">
                    <li>{TEXT.note1}</li>
                    <li>{TEXT.note2}</li>
                    <li>{TEXT.note3}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default FilamentPage;
